import React, { useState, useEffect } from 'react';
import { useNavigate } from 'react-router-dom';
import {
  Box,
  Typography,
  Paper,
  TextField,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TableRow,
  InputAdornment,
  CircularProgress,
  Chip,
} from '@mui/material';
import {
  PersonOffOutlined,
  SearchOutlined,
  StarOutlined,
} from '@mui/icons-material';
import { reportService, LapsedPartnerReport } from '../../services/reportService';

const LapsedUserReport: React.FC = () => {
  const navigate = useNavigate();
  const [partners, setPartners] = useState<LapsedPartnerReport[]>([]);
  const [filteredPartners, setFilteredPartners] = useState<LapsedPartnerReport[]>([]);
  const [searchQuery, setSearchQuery] = useState('');
  const [loading, setLoading] = useState(true);
  const [totalCount, setTotalCount] = useState(0);

  useEffect(() => {
    fetchLapsedPartners();
  }, []);

  useEffect(() => {
    filterPartners();
  }, [searchQuery, partners]);

  const fetchLapsedPartners = async () => {
    try {
      const response = await reportService.getLapsedPartners();
      setPartners(response.partners || []);
      setTotalCount(response.total_count || 0);
    } catch (error) {
      console.error('Error fetching lapsed partners:', error);
    } finally {
      setLoading(false);
    }
  };

  const filterPartners = () => {
    if (!Array.isArray(partners)) {
      setFilteredPartners([]);
      return;
    }
    if (!searchQuery.trim()) {
      setFilteredPartners(partners);
      return;
    }

    const query = searchQuery.toLowerCase().trim();
    const filtered = partners.filter((partner) => {
      return partner.partner_name.toLowerCase().includes(query);
    });
    setFilteredPartners(filtered);
  };

  const formatDate = (dateString: string | null): string => {
    if (!dateString) return 'Never';
    const date = new Date(dateString);
    return date.toLocaleDateString('en-IN', {
      day: '2-digit',
      month: 'short',
      year: 'numeric',
    });
  };

  const getDaysChipColor = (days: number): 'error' | 'warning' | 'default' => {
    if (days > 60) return 'error';
    if (days > 30) return 'warning';
    return 'default';
  };

  const renderRating = (rating: number) => {
    return (
      <Box sx={{ display: 'flex', alignItems: 'center', gap: 0.5 }}>
        {[1, 2, 3, 4, 5].map((star) => (
          <StarOutlined
            key={star}
            sx={{
              fontSize: 16,
              color: star <= rating ? 'warning.main' : 'grey.300',
            }}
          />
        ))}
      </Box>
    );
  };

  return (
    <Paper sx={{ p: 2, m: 0 }}>
      {/* Header */}
      <Box sx={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', mb: 2 }}>
        <Box sx={{ display: 'flex', alignItems: 'center' }}>
          <PersonOffOutlined sx={{ fontSize: 28, mr: 1.5, color: 'warning.main' }} />
          <Typography variant="h6" fontWeight="bold">
            Lapsed User Report
          </Typography>
        </Box>
        {!loading && (
          <Chip
            label={`${totalCount} Lapsed Users`}
            color="warning"
            variant="outlined"
            size="small"
          />
        )}
      </Box>

      {/* Description */}
      <Typography variant="body2" color="text.secondary" sx={{ mb: 2 }}>
        Users who haven't placed orders within their expected order cycle period.
      </Typography>

      {/* Search Bar */}
      <TextField
        size="small"
        fullWidth
        placeholder="Search by name..."
        value={searchQuery}
        onChange={(e) => setSearchQuery(e.target.value)}
        sx={{ mb: 2 }}
        InputProps={{
          startAdornment: (
            <InputAdornment position="start">
              <SearchOutlined color="action" fontSize="small" />
            </InputAdornment>
          ),
        }}
      />

      {/* Results Count */}
      {searchQuery && (
        <Typography variant="body2" color="text.secondary" sx={{ mb: 1.5 }}>
          Found {filteredPartners.length} user{filteredPartners.length !== 1 ? 's' : ''} matching "{searchQuery}"
        </Typography>
      )}

      {/* Table */}
      {loading ? (
        <Box sx={{ display: 'flex', justifyContent: 'center', py: 3 }}>
          <CircularProgress size={28} />
        </Box>
      ) : (
        <TableContainer>
          <Table size="small">
            <TableHead>
              <TableRow sx={{ bgcolor: 'grey.100' }}>
                <TableCell sx={{ fontWeight: 'bold' }}>Partner Name</TableCell>
                <TableCell sx={{ fontWeight: 'bold' }}>Last Order Date</TableCell>
                <TableCell sx={{ fontWeight: 'bold' }} align="center">Days Since Order</TableCell>
                <TableCell sx={{ fontWeight: 'bold' }} align="center">Order Cycle (Days)</TableCell>
                <TableCell sx={{ fontWeight: 'bold' }} align="center">Rating</TableCell>
              </TableRow>
            </TableHead>
            <TableBody>
              {filteredPartners.length === 0 ? (
                <TableRow>
                  <TableCell colSpan={5} align="center" sx={{ py: 3 }}>
                    <Typography variant="body2" color="text.secondary">
                      {searchQuery ? 'No users found matching your search' : 'No lapsed users found'}
                    </Typography>
                  </TableCell>
                </TableRow>
              ) : (
                filteredPartners.map((partner) => (
                  <TableRow key={partner.partner_id} hover>
                    <TableCell>
                      <Typography
                        variant="body2"
                        fontWeight="medium"
                        sx={{
                          color: 'primary.main',
                          cursor: 'pointer',
                          '&:hover': { textDecoration: 'underline' },
                        }}
                        onClick={() => navigate(`/customers/${partner.partner_id}`)}
                      >
                        {partner.partner_name}
                      </Typography>
                    </TableCell>
                    <TableCell>
                      <Typography variant="body2">{formatDate(partner.last_order_date)}</Typography>
                    </TableCell>
                    <TableCell align="center">
                      {partner.days_since_order < 0 ? (
                        '-'
                      ) : (
                        <Chip
                          label={`${partner.days_since_order} days`}
                          size="small"
                          color={getDaysChipColor(partner.days_since_order)}
                        />
                      )}
                    </TableCell>
                    <TableCell align="center">
                      <Typography variant="body2">{partner.order_cycle_days}</Typography>
                    </TableCell>
                    <TableCell align="center">{renderRating(partner.rating)}</TableCell>
                  </TableRow>
                ))
              )}
            </TableBody>
          </Table>
        </TableContainer>
      )}

      {/* Total Count */}
      {!loading && !searchQuery && filteredPartners.length > 0 && (
        <Typography variant="body2" color="text.secondary" sx={{ mt: 1.5 }}>
          Showing {filteredPartners.length} of {totalCount} lapsed user{totalCount !== 1 ? 's' : ''}
        </Typography>
      )}
    </Paper>
  );
};

export default LapsedUserReport;
